// ==================== Constants & taxonomies ====================

// All 16 regions of Ghana (as of 2019 regional reorganisation)
export const GHANA_REGIONS = [
  'Ahafo',
  'Ashanti',
  'Bono',
  'Bono East',
  'Central',
  'Eastern',
  'Greater Accra',
  'North East',
  'Northern',
  'Oti',
  'Savannah',
  'Upper East',
  'Upper West',
  'Volta',
  'Western',
  'Western North',
];

// Ghana court hierarchy (1992 Constitution Art. 126 + Courts Act 1993, Act 459).
// Used by the matter form's Jurisdiction dropdown so the firm doesn't end up
// with "Circuit Court", "circuit court", "circuit ct" all in the same column —
// search, reporting and procedural-rule lookups all break if the values aren't
// canonical. Order is highest-to-lowest court (firms reach for the higher
// courts more often in filing). Values double as labels because the canonical
// names are short and self-explanatory.
//
// "High Court" is split by division because in practice Ghanaian lawyers treat
// the Commercial Court as a separate forum (different rules, different filing
// fees, different listing rhythm) even though it's technically a division.
// Same logic for Land, Human Rights, Labour, Financial / Probate, and
// Divorce / Matrimonial.
export const GHANA_JURISDICTIONS = [
  // Superior Courts of Judicature
  'Supreme Court',
  'Court of Appeal',
  'High Court (Commercial)',
  'High Court (Land)',
  'High Court (Human Rights)',
  'High Court (Labour)',
  'High Court (Financial / Probate)',
  'High Court (Divorce & Matrimonial)',
  'High Court (General)',
  // Lower Courts (Act 459)
  'Circuit Court',
  'District Court',
  'Juvenile Court',
  'Family Tribunal',
  // Specialised forums
  'Industrial Court / Labour Commission',
  'National House of Chiefs',
  'Public Tribunal',
  // Catch-all when the matter sits outside the court system or
  // involves a body not listed above (arbitration panels, regulatory
  // tribunals like SEC, the National Media Commission, etc.)
  'Other',
];

export const PRACTICE_AREAS = [
  'Criminal Defense', 'Family Law', 'Personal Injury', 'Estate Planning',
  'Corporate/Business', 'Real Estate', 'Immigration', 'Employment',
  'Intellectual Property', 'Bankruptcy', 'Tax', 'Civil Litigation',
  'Contract Dispute', 'Wills & Trusts', 'Other',
];

export const MATTER_STATUS = [
  { value: 'open',        label: 'Open',        tone: 'green'  },
  { value: 'in_progress', label: 'In Progress', tone: 'blue'   },
  { value: 'pending',     label: 'Pending',     tone: 'amber'  },
  { value: 'on_hold',     label: 'On Hold',     tone: 'slate'  },
  { value: 'completed',   label: 'Completed',   tone: 'emerald'},
  { value: 'closed',      label: 'Closed',      tone: 'slate'  },
  { value: 'archived',    label: 'Archived',    tone: 'slate'  },
];

export const MATTER_PRIORITY = [
  { value: 'low', label: 'Low', tone: 'slate' },
  { value: 'medium', label: 'Medium', tone: 'blue' },
  { value: 'high', label: 'High', tone: 'amber' },
  { value: 'critical', label: 'Critical', tone: 'red' },
];

export const FEE_ARRANGEMENTS = [
  { value: 'hourly',      label: 'Hourly' },
  { value: 'flat_fee',    label: 'Flat Fee' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'retainer',    label: 'Retainer' },
  { value: 'hybrid',      label: 'Hybrid' },
  { value: 'pro_bono',    label: 'Pro Bono' },
];

export const EVENT_TYPES = [
  { value: 'court', label: 'Court', color: '#dc2626' },
  { value: 'hearing', label: 'Hearing', color: '#ea580c' },
  { value: 'deposition', label: 'Deposition', color: '#9333ea' },
  { value: 'deadline', label: 'Deadline', color: '#dc2626' },
  { value: 'appointment', label: 'Appointment', color: '#2563eb' },
  { value: 'internal', label: 'Internal', color: '#059669' },
  { value: 'reminder', label: 'Reminder', color: '#0891b2' },
  { value: 'other', label: 'Other', color: '#64748b' },
];

// Invitable employee roles for the bulk-invite + add-staff forms.
// Ordered loosely by scope of system access:
//   - Permission-bearing roles (seeded by migration 031 with firm_role_permissions):
//     Admin, Partner, Attorney, Associate, Paralegal, Clerk, IT
//   - Descriptive / legacy text-only roles kept for firms that already use them
//     (no permission rows; the system defaults their permissions to Associate
//     via the migration-031 backfill):
//     Of Counsel, Legal Secretary, Legal Assistant, Administrator, Billing Clerk
//
// KEEP IN SYNC with the ROLES constant in src/pages/SettingsPage.jsx and the
// PLAN_CAPACITY mapping in supabase/functions/paystack-webhook/index.ts.
export const EMPLOYEE_ROLES = [
  'Admin',
  'Partner',
  'Attorney',
  'Associate',
  'Paralegal',
  'Clerk',
  'IT',
  'Of Counsel',
  'Legal Secretary',
  'Legal Assistant',
  'Administrator',
  'Billing Clerk',
];

export const CONTACT_TYPES = [
  { value: 'client', label: 'Client' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'opposing_counsel', label: 'Opposing Counsel' },
  { value: 'witness', label: 'Witness' },
  { value: 'expert', label: 'Expert' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'court', label: 'Court' },
  { value: 'other', label: 'Other' },
];

export const LEAD_STATUS = [
  { value: 'new', label: 'New', tone: 'blue' },
  { value: 'contacted', label: 'Contacted', tone: 'amber' },
  { value: 'qualified', label: 'Qualified', tone: 'green' },
  { value: 'converted', label: 'Converted', tone: 'indigo' },
  { value: 'rejected', label: 'Rejected', tone: 'red' },
  { value: 'archived', label: 'Archived', tone: 'slate' },
];

// Ghana deadline rule templates based on CI 47 (High Court Civil Procedure Rules),
// Court of Appeal Rules (CI 19), Supreme Court Rules (CI 16), and common firm policy.
// offsetDays is calculated from the trigger date (e.g. date of service, date of judgment).
export const DEADLINE_RULE_TEMPLATES = [
  // ── High Court (CI 47) ──────────────────────────────────────────────────────
  { value: 'entry_of_appearance',   label: 'Entry of Appearance (CI 47, O.9)',         offsetDays: 8,   businessDays: false, jurisdiction: 'High Court' },
  { value: 'statement_of_defence',  label: 'Statement of Defence (CI 47, O.11)',        offsetDays: 14,  businessDays: false, jurisdiction: 'High Court' },
  { value: 'reply_to_defence',      label: 'Reply to Statement of Defence (CI 47)',     offsetDays: 7,   businessDays: false, jurisdiction: 'High Court' },
  { value: 'application_directions',label: 'Application for Directions (CI 47, O.32)', offsetDays: 14,  businessDays: false, jurisdiction: 'High Court' },
  { value: 'discovery_compliance',  label: 'Discovery / Inspection Compliance (CI 47)', offsetDays: 14,  businessDays: false, jurisdiction: 'High Court' },
  { value: 'default_judgment',      label: 'Default Judgment Application (CI 47)',      offsetDays: 8,   businessDays: false, jurisdiction: 'High Court' },
  { value: 'summary_judgment_gh',   label: 'Summary Judgment Application (CI 47)',      offsetDays: 14,  businessDays: false, jurisdiction: 'High Court' },
  // ── Appeals ─────────────────────────────────────────────────────────────────
  { value: 'appeal_court_of_appeal',label: 'Notice of Appeal — Court of Appeal (CI 19)', offsetDays: 90, businessDays: false, jurisdiction: 'Court of Appeal' },
  { value: 'appeal_supreme_court',  label: 'Notice of Appeal — Supreme Court (CI 16)',   offsetDays: 90, businessDays: false, jurisdiction: 'Supreme Court' },
  { value: 'appeal_interlocutory',  label: 'Interlocutory Appeal (CI 19)',                offsetDays: 21, businessDays: false, jurisdiction: 'Court of Appeal' },
  // ── Firm Policy ─────────────────────────────────────────────────────────────
  { value: 'sol_buffer',            label: 'Limitation Period Buffer (Internal)',         offsetDays: -30, businessDays: false, jurisdiction: 'Firm Policy' },
  { value: 'client_response',       label: 'Client Response / Instructions Due',          offsetDays: 7,   businessDays: true,  jurisdiction: 'Firm Policy' },
];

export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

// Ghana statutory tax levies (rates as a percentage of the taxable amount)
export const GHANA_TAX_LEVIES = [
  { value: 'vat',     label: 'VAT (Standard)',                     rate: 15.0 },
  { value: 'nhil',    label: 'NHIL (National Health Insurance Levy)', rate: 2.5  },
  { value: 'getfund', label: 'GETFund Levy',                       rate: 2.5  },
  { value: 'covid19', label: 'COVID-19 Health Recovery Levy',      rate: 1.0  },
];

// Standard reasons for closing a matter
export const CLOSING_REASONS = [
  { value: 'settled',            label: 'Matter settled' },
  { value: 'judgment',           label: 'Judgment obtained' },
  { value: 'acquittal',          label: 'Acquittal / not guilty verdict' },
  { value: 'resolved',           label: 'Resolved without litigation' },
  { value: 'client_withdrew',    label: 'Client withdrew instructions' },
  { value: 'client_deceased',    label: 'Client deceased' },
  { value: 'transferred',        label: 'Matter transferred to another firm' },
  { value: 'non_payment',        label: 'Closed due to non-payment / fee dispute' },
  { value: 'conflict',           label: 'Conflict of interest discovered' },
  { value: 'dismissed',          label: 'Case dismissed by court' },
  { value: 'statute_expired',    label: 'Statute of limitations expired' },
  { value: 'no_instructions',    label: 'Client unresponsive / no further instructions' },
  { value: 'other',              label: 'Other' },
];
